import React, { useCallback, useEffect, useState } from 'react';
import { planApi, formatMoney, formatDate, INTERVAL_LABELS } from './planApi';
import { Badge, Modal, ConfirmModal, Field, Toast, useToast } from './PlanUi';
import PlanVersionEditor from './PlanVersionEditor';
import SubscriptionManager from './SubscriptionManager';
import SubscriptionPayments from './SubscriptionPayments';
import BillingAutomation from './BillingAutomation';
import PartnerUsage from './PartnerUsage';
import '../style/AdminPlans.css';

// ─── New plan modal ──────────────────────────────────────────────────────────
const NewPlanModal = ({ onClose, onCreated, notify }) => {
  const [form, setForm] = useState({ name: '', planId: '', category: 'self_serve' });
  const [idTouched, setIdTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await planApi.createPlan(form);
      notify(`Plan "${res.plan.name}" created with draft v1`);
      onCreated(res.plan.planId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New plan"
      subtitle="Creates the plan and an editable draft (version 1)"
      onClose={onClose}
      footer={
        <>
          <button className="pm-btn pm-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn--primary" onClick={submit} disabled={busy || !form.name || !form.planId}>{busy ? 'Creating…' : 'Create plan'}</button>
        </>
      }
    >
      <form onSubmit={submit} className="pm-form">
        <Field label="Plan name">
          <input className="pm-input" autoFocus value={form.name} maxLength={80}
            onChange={e => setForm(f => ({ ...f, name: e.target.value, planId: idTouched ? f.planId : slugify(e.target.value) }))}
            placeholder="e.g. Growth" />
        </Field>
        <Field label="Plan ID" hint="Permanent, machine-readable identifier. Cannot be changed later." error={error}>
          <input className="pm-input pm-mono" value={form.planId}
            onChange={e => { setIdTouched(true); setForm(f => ({ ...f, planId: slugify(e.target.value) })); }}
            placeholder="growth" />
        </Field>
        <Field label="Category">
          <select className="pm-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            <option value="self_serve">Self-serve</option>
            <option value="sales_assisted">Sales-assisted</option>
            <option value="custom">Custom / Enterprise</option>
          </select>
        </Field>
      </form>
    </Modal>
  );
};

// ─── Plan card ───────────────────────────────────────────────────────────────
const PlanCard = ({ plan, onOpen }) => (
  <article className="pm-plan-card" onClick={onOpen} tabIndex={0} onKeyDown={e => e.key === 'Enter' && onOpen()}>
    <div className="pm-plan-card__top">
      <Badge status={plan.status} />
      {plan.draftVersion && <Badge tone="warn">Draft v{plan.draftVersion}</Badge>}
      {plan.current?.identity?.isFeatured && <Badge tone="info">Featured</Badge>}
    </div>
    <h3 className="pm-plan-card__name">{plan.displayName}</h3>
    <p className="pm-plan-card__id pm-mono">{plan.planId}</p>
    <div className="pm-plan-card__price">
      {plan.current ? (
        plan.current.billingOptions.filter(o => o.isActive !== false).length ? (
          plan.current.billingOptions.filter(o => o.isActive !== false).map(o => (
            <div key={o.interval}>
              <strong>{formatMoney(o.amountMinor, plan.current.currency)}</strong>
              <span> / {INTERVAL_LABELS[o.interval]?.toLowerCase()}</span>
            </div>
          ))
        ) : <span className="pm-muted">Contract pricing</span>
      ) : <span className="pm-muted">Not published yet</span>}
    </div>
    <div className="pm-plan-card__foot">
      <span>{plan.current ? `v${plan.current.version} on sale` : 'No live version'}</span>
      <span><strong>{plan.activeSubscribers}</strong> subscriber{plan.activeSubscribers === 1 ? '' : 's'}</span>
    </div>
  </article>
);

// ─── Plan detail (versions + editor) ─────────────────────────────────────────
const PlanDetail = ({ planId, registry, onBack, notify }) => {
  const [plan, setPlan] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (selectVersion) => {
    try {
      const res = await planApi.getPlan(planId);
      setPlan(res.plan);
      setVersions(res.versions);
      setSelected(prev => {
        const want = selectVersion !== undefined ? selectVersion : prev;
        if (want && res.versions.some(v => v.version === want)) return want;
        const draft = res.versions.find(v => v.status === 'draft');
        return draft?.version ?? res.plan.currentPublishedVersion ?? res.versions[0]?.version ?? null;
      });
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const draft = versions.find(v => v.status === 'draft');
  const selectedVersion = versions.find(v => v.version === selected);

  const newDraft = async (fromVersion) => {
    setBusy(true);
    try {
      const res = await planApi.createVersion(planId, { fromVersion });
      notify(`Draft v${res.version.version} created from v${fromVersion}`);
      await load(res.version.version);
    } catch (err) {
      notify(err.message, 'error');
      if (err.data?.draftVersion) setSelected(err.data.draftVersion);
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    setBusy(true);
    try {
      await planApi.updatePlan(planId, { name: newName });
      notify('Plan renamed');
      setRenaming(false);
      await load();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleRetire = async () => {
    setBusy(true);
    try {
      const status = plan.status === 'active' ? 'retired' : 'active';
      await planApi.updatePlan(planId, { status });
      notify(status === 'retired' ? 'Plan retired — no new subscriptions can be created' : 'Plan reactivated');
      setConfirmRetire(false);
      await load();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="pm-empty"><span className="pm-spinner" /> Loading plan…</div>;
  if (error) return <div className="pm-callout pm-callout--error">{error} <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={onBack}>Back</button></div>;

  return (
    <div className="pm-detail">
      <div className="pm-detail__head">
        <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={onBack}>← All plans</button>
        <div className="pm-detail__title">
          {renaming ? (
            <div className="pm-inline-form">
              <input className="pm-input" value={newName} onChange={e => setNewName(e.target.value)} autoFocus maxLength={80} />
              <button className="pm-btn pm-btn--primary pm-btn--sm" onClick={saveName} disabled={busy || !newName.trim()}>Save</button>
              <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setRenaming(false)}>Cancel</button>
            </div>
          ) : (
            <>
              <h2>{plan.name}</h2>
              <button className="pm-link" onClick={() => { setNewName(plan.name); setRenaming(true); }}>Rename</button>
            </>
          )}
          <span className="pm-mono pm-muted">{plan.planId}</span>
          <Badge status={plan.status} />
        </div>
        <div className="pm-detail__actions">
          <button className={`pm-btn pm-btn--ghost ${plan.status === 'active' ? 'pm-btn--danger-text' : ''}`} onClick={() => setConfirmRetire(true)}>
            {plan.status === 'active' ? 'Retire plan' : 'Reactivate plan'}
          </button>
        </div>
      </div>

      <div className="pm-detail__layout">
        <aside className="pm-versions">
          <div className="pm-versions__head">
            <span>Versions</span>
            {!draft && versions.length > 0 && (
              <button className="pm-btn pm-btn--primary pm-btn--sm" disabled={busy}
                onClick={() => newDraft(selected || plan.currentPublishedVersion || versions[0].version)}>
                + New draft
              </button>
            )}
          </div>
          {!draft && versions.length > 0 && <p className="pm-muted pm-small pm-versions__hint">New draft copies the selected version (v{selected}).</p>}
          {versions.map(v => (
            <button key={v.version} className={`pm-version ${selected === v.version ? 'pm-version--active' : ''}`} onClick={() => setSelected(v.version)}>
              <div className="pm-version__row">
                <strong>v{v.version}</strong>
                <Badge status={v.status} />
                {plan.currentPublishedVersion === v.version && <Badge tone="info">On sale</Badge>}
              </div>
              <div className="pm-version__meta">
                {v.status === 'draft' ? `Edited ${formatDate(v.updatedAt)}` : `Published ${formatDate(v.lifecycle?.publishedAt)}`}
                {v.activeSubscribers ? ` · ${v.activeSubscribers} subs` : ''}
              </div>
              {v.changeNote && <div className="pm-version__note">{v.changeNote}</div>}
            </button>
          ))}
        </aside>

        <div className="pm-detail__main">
          {selectedVersion ? (
            <PlanVersionEditor
              key={`${selectedVersion.version}-${selectedVersion.updatedAt}`}
              registry={registry}
              planId={planId}
              version={selectedVersion}
              isCurrent={plan.currentPublishedVersion === selectedVersion.version}
              onChanged={(v) => load(v)}
              notify={notify}
            />
          ) : (
            <div className="pm-empty">
              No versions yet.
              <button className="pm-btn pm-btn--primary" onClick={() => newDraft(undefined)} disabled={busy}>Create draft</button>
            </div>
          )}
        </div>
      </div>

      {confirmRetire && (
        <ConfirmModal
          title={plan.status === 'active' ? `Retire ${plan.name}?` : `Reactivate ${plan.name}?`}
          danger={plan.status === 'active'}
          confirmLabel={plan.status === 'active' ? 'Retire' : 'Reactivate'}
          busy={busy}
          onConfirm={toggleRetire}
          onClose={() => setConfirmRetire(false)}
          message={plan.status === 'active'
            ? 'The plan will stop being offered and cannot be assigned to new owners. Existing subscribers are not affected.'
            : 'The plan can be assigned to owners again.'}
        />
      )}
    </div>
  );
};

// ─── Plans catalogue ─────────────────────────────────────────────────────────
const PlansCatalogue = ({ onOpen, notify }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await planApi.listPlans();
      setPlans(res.plans);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = plans.filter(p => showRetired || p.status === 'active');
  const retiredCount = plans.filter(p => p.status === 'retired').length;

  return (
    <div>
      <div className="pm-toolbar">
        <div className="pm-muted pm-small">
          {plans.length} plan{plans.length === 1 ? '' : 's'} · each plan has immutable published versions; edit via drafts
        </div>
        <div className="pm-toolbar__right">
          {retiredCount > 0 && (
            <label className="pm-check"><input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} /> Show retired ({retiredCount})</label>
          )}
          <button className="pm-btn pm-btn--primary" onClick={() => setShowNew(true)}>+ New plan</button>
        </div>
      </div>

      {error && <div className="pm-callout pm-callout--error">{error}</div>}
      {loading ? (
        <div className="pm-plan-grid">{[0, 1, 2].map(i => <div key={i} className="pm-plan-card pm-plan-card--skeleton" />)}</div>
      ) : visible.length === 0 ? (
        <div className="pm-empty">
          <p>No plans yet. Create your first plan — e.g. Starter, Growth, Premium, Enterprise.</p>
          <button className="pm-btn pm-btn--primary" onClick={() => setShowNew(true)}>+ New plan</button>
        </div>
      ) : (
        <div className="pm-plan-grid">
          {visible.map(p => <PlanCard key={p.planId} plan={p} onOpen={() => onOpen(p.planId)} />)}
        </div>
      )}

      {showNew && <NewPlanModal notify={notify} onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); onOpen(id); }} />}
    </div>
  );
};

// ─── Root ────────────────────────────────────────────────────────────────────
const PlanManagement = () => {
  const [tab, setTab] = useState('plans');
  const [openPlanId, setOpenPlanId] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [registryError, setRegistryError] = useState('');
  const [toast, notify] = useToast();

  useEffect(() => {
    planApi.registry()
      .then(res => setRegistry(res.registry))
      .catch(err => setRegistryError(err.message));
  }, []);

  if (registryError) return <div className="pm-root"><div className="pm-callout pm-callout--error">Could not load plan configuration: {registryError}</div></div>;
  if (!registry) return <div className="pm-root"><div className="pm-empty"><span className="pm-spinner" /> Loading…</div></div>;

  return (
    <div className="pm-root">
      <Toast toast={toast} />
      <div className="pm-tabs">
        <button className={`pm-tab ${tab === 'plans' ? 'pm-tab--active' : ''}`} onClick={() => setTab('plans')}>Plans</button>
        <button className={`pm-tab ${tab === 'subscriptions' ? 'pm-tab--active' : ''}`} onClick={() => setTab('subscriptions')}>Subscriptions</button>
        <button className={`pm-tab ${tab === 'payments' ? 'pm-tab--active' : ''}`} onClick={() => setTab('payments')}>Online payments</button>
        <button className={`pm-tab ${tab === 'usage' ? 'pm-tab--active' : ''}`} onClick={() => setTab('usage')}>Partner usage</button>
        <button className={`pm-tab ${tab === 'automation' ? 'pm-tab--active' : ''}`} onClick={() => setTab('automation')}>Automation</button>
      </div>

      {tab === 'plans' && (
        openPlanId
          ? <PlanDetail planId={openPlanId} registry={registry} notify={notify} onBack={() => setOpenPlanId(null)} />
          : <PlansCatalogue notify={notify} onOpen={setOpenPlanId} />
      )}
      {tab === 'subscriptions' && <SubscriptionManager registry={registry} notify={notify} />}
      {tab === 'payments' && <SubscriptionPayments notify={notify} />}
      {tab === 'usage' && <PartnerUsage notify={notify} />}
      {tab === 'automation' && <BillingAutomation notify={notify} />}
    </div>
  );
};

export default PlanManagement;
